import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, CornerDownLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { fmtAxvTrim, IMAGES } from "../lib/tokens";
import { useWorld } from "../state/World";
import MelzFigure from "../components/MelzFigure";
import LiveToggle from "../components/LiveToggle";
import { SCREEN_RECT } from "../lib/beats";

// Push in until the glass fills ~94% of the frame. Derived from the measured
// screen rect, so re-measuring one plate re-aims the whole move.
const SCREEN_CY = (SCREEN_RECT.top + SCREEN_RECT.bottom) / 2;
const SCREEN_ZOOM = 94 / (SCREEN_RECT.right - SCREEN_RECT.left);


const PROMPTS = {
  what: [
    "Publish the book I've been circling for nine years",
    "Get the consultancy I keep describing to actually exist",
    "Rebuild my knees and walk the coast path again",
  ],
  where: [
    "In the hands of real readers, not my desk drawer",
    "In front of four paying clients who found me, not the reverse",
    "On the coast path itself, from Minehead to Padstow",
  ],
};

const STAGE_STEP = { greet: 1, what: 1, slice: 2, where: 3, when: 4, done: 4 };

/** The wall display. Every pixel of the game lives in here. */
const Screen = ({ children, live, thinking, mode }) => (
  <div
    className="relative h-full w-full overflow-hidden rounded-[10px]"
    style={{
      background:
        "radial-gradient(ellipse 90% 80% at 50% 55%, rgba(9,24,36,0.62) 0%, rgba(5,9,15,0.86) 68%, rgba(4,6,11,0.94) 100%)",
      boxShadow: "0 0 160px -30px rgba(255,196,120,0.22)",
    }}
    data-testid="cove-screen"
  >
    {/* the horizon glow from the founder's plate, recreated live */}
    <motion.div
      className="pointer-events-none absolute inset-x-0"
      aria-hidden
      animate={{ opacity: thinking ? [0.55, 1, 0.55] : live ? 0.9 : 0.6 }}
      transition={{ duration: 2.4, repeat: thinking ? Infinity : 0 }}
      style={{
        top: "38%",
        height: "34%",
        background: live
          ? "radial-gradient(ellipse at 50% 60%, rgba(120,215,255,0.55), rgba(10,26,38,0) 70%)"
          : "radial-gradient(ellipse at 50% 60%, rgba(150,205,255,0.4), rgba(10,26,38,0) 70%)",
      }}
    />
    {/* grid floor */}
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 opacity-[0.14]"
      aria-hidden
      style={{
        backgroundImage:
          "linear-gradient(rgba(160,220,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(160,220,255,0.6) 1px, transparent 1px)",
        backgroundSize: "38px 26px",
        transform: "perspective(240px) rotateX(58deg)",
        transformOrigin: "bottom",
      }}
    />
    {/* scanlines + glass */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.16]"
      aria-hidden
      style={{
        background:
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, transparent 1px, transparent 3px)",
      }}
    />
    <div
      className="pointer-events-none absolute inset-0"
      aria-hidden
      style={{
        background:
          "linear-gradient(115deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0) 42%)",
      }}
    />
    <MelzFigure mode={mode} live={live} thinking={thinking} />
    <div className="relative z-10 flex h-full flex-col md:pl-[27%]">{children}</div>
  </div>
);

export default function Cove() {
  const { me, live, isLive, mark, refresh } = useWorld();
  const nav = useNavigate();
  const [seated, setSeated] = useState(false);
  const [wake, setWake] = useState("off");
  const [turns, setTurns] = useState([]);
  const [stage, setStage] = useState("greet");
  const [way, setWay] = useState({});
  const [mode, setMode] = useState(null);
  const [options, setOptions] = useState([]);
  const [liveSpent, setLiveSpent] = useState(false);
  const [cadences, setCadences] = useState([]);
  const [source, setSource] = useState("demo");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [cutting, setCutting] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    mark("playforward", "/playforward/cove");
  }, [mark]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: 1e6, behavior: "smooth" });
  }, [turns, busy]);

  const send = useCallback(
    async (message) => {
      setBusy(true);
      if (message) setTurns((t) => [...t, { role: "user", text: message }]);
      try {
        const { data } = await api.post("/melzai/cove", { message, live: isLive() });
        setStage(data.stage);
        setWay(data.way || {});
        setMode(data.mode);
        setSource(data.source);
        setOptions(data.options || []);
        setCadences(data.cadences || []);
        setLiveSpent(Boolean(data.live_spent));
        window.dispatchEvent(new Event("melz-live-used"));
        setTurns((t) => [...t, { role: "melz", text: data.reply }]);
      } catch (e) {
        toast.error("MelzAI lost the thread. Try once more.");
      } finally {
        setBusy(false);
      }
    },
    [isLive]
  );

  const sit = async () => {
    setSeated(true);
    const { data } = await api.get("/melzai/cove");
    if (data?.turns?.length) {
      setTurns(data.turns.map((t) => ({ role: t.role, text: t.text })));
      setStage(data.stage);
      setWay(data.way || {});
      setOptions(data.options || []);
      setCadences(data.cadences || []);
    } else {
      send("");
    }
  };

  const cutPlayCard = async () => {
    setCutting(true);
    try {
      const { data } = await api.post("/playforward/playcard", {
        aim: way.what || "An unnamed aim",
        slice: way.slice || "",
        cadence: way.cadence || "",
        cadence_name: way.cadence_name || "",
        deadline: way.deadline || "",
        ...Object.fromEntries(
          ["what", "where", "when", "that", "there", "then"].map((k) => [k, way[k] || ""])
        ),
      });
      await refresh();
      toast.success("PlayCard cut. Nobody else is on it.");
      nav("/playforward/play", { state: { playcardId: data.id } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The card would not cut.");
    } finally {
      setCutting(false);
    }
  };

  // push in -> blue settles to black -> MelzAI wakes
  useEffect(() => {
    if (!seated) {
      setWake("off");
      return;
    }
    setWake("zooming");
    const toDark = setTimeout(() => setWake("dark"), 1450);
    const toAwake = setTimeout(() => setWake("awake"), 2350);
    return () => {
      clearTimeout(toDark);
      clearTimeout(toAwake);
    };
  }, [seated]);

  const complete = stage === "done" && Boolean(way.that);
  const chips = options.length ? options : PROMPTS[stage] || [];
  const step = STAGE_STEP[stage] || 1;
  const lastMelz = [...turns].reverse().find((t) => t.role === "melz");

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#05060a]">
      {/* the room — the founder's plate, never carrying text */}
      <img
        src={IMAGES.pfCoveSeat}
        alt="The Cove"
        data-testid="cove-plate"
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          transformOrigin: `50% ${SCREEN_CY}%`,
          transition: "transform 1400ms cubic-bezier(0.2,0.8,0.2,1)",
          transform: seated ? `translateY(${50 - SCREEN_CY}%) scale(${SCREEN_ZOOM})` : "none",
        }}
      />

      {/* The blue settles to black — the display coming to life. Feathered at
          the edges so the ends dissolve into the founder's plate instead of
          stamping a rectangle over it. Replaced by a screen-dark plate, or a
          talking MelzAI video, the moment either lands. */}
      <motion.div
        aria-hidden
        data-testid="screen-dark"
        className="absolute -inset-[8%] z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: wake === "dark" || wake === "awake" ? 1 : 0 }}
        transition={{ duration: 1.1, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(ellipse 58% 60% at 50% 50%, #04060b 0%, #04060b 64%, rgba(4,6,11,0.86) 80%, rgba(4,6,11,0.42) 92%, rgba(4,6,11,0) 100%)",
        }}
      />

      <button
        type="button"
        data-testid="back-to-arcade"
        onClick={() => nav("/playforward")}
        className="tracked absolute left-6 top-[88px] z-30 flex items-center gap-2 text-[9px] text-white/55 transition-colors duration-200 hover:text-[var(--arcade-cyan)] min-[641px]:top-6 md:left-10 md:top-10"
      >
        <ArrowLeft size={12} /> The floor
      </button>

      {/* ---------------- take the chair ---------------- */}
      <AnimatePresence>
        {!seated && (
          <motion.div
            className="absolute inset-0 z-20 flex items-end justify-start p-8 md:items-center md:p-16"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1 }}
              className="max-w-md"
            >
              <div className="tracked mb-4 text-[10px] text-[var(--stage-gold)]/85">
                PlayForward · the Cove
              </div>
              <h1 className="serif mb-5 text-4xl font-light leading-tight text-[#f4ece0] sm:text-5xl">
                There is one chair.
                <br />
                <span className="italic text-[var(--stage-gold)]">It is yours.</span>
              </h1>
              <p className="mb-8 text-sm leading-relaxed text-white/65">
                Sit down and the screen wakes up. MelzAI plays on the screen — you play
                from the chair.
              </p>
              <motion.button
                type="button"
                data-testid="sit-in-chair"
                onClick={sit}
                className="btn btn-gold"
                animate={{
                  boxShadow: [
                    "0 0 22px -10px rgba(224,164,74,0.4)",
                    "0 0 46px -6px rgba(224,164,74,0.75)",
                    "0 0 22px -10px rgba(224,164,74,0.4)",
                  ],
                }}
                transition={{ duration: 3.4, repeat: Infinity }}
              >
                Take the chair
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------------- the screen ---------------- */}
      {wake === "awake" && (
        <motion.div
          className="absolute inset-x-4 bottom-5 top-[88px] z-20 md:inset-x-8 md:bottom-8 md:top-[88px] lg:inset-x-14 lg:bottom-12 lg:top-24"
          initial={{ opacity: 0, scale: 0.965 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <Screen live={live} thinking={busy} mode={mode}>
            {/* screen chrome */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 md:px-12">
              <div className="flex items-center gap-3">
                <div>
                  <div className="tracked text-[9px] text-[#bfe6ff]">MelzAI</div>
                  <div
                    className="mono text-[8px] text-white/40"
                    data-testid="melzai-source"
                  >
                    {liveSpent
                      ? "live proven here \u00b7 back to demo mode"
                      : source === "live"
                      ? "live claude call"
                      : "pre-vetted demo mode"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="mono text-[9px] text-white/35" data-testid="way-step">
                  the Way · <span className="text-[#bfe6ff]">{step}/4</span>
                </div>
                <div className="text-right" data-testid="pouch-hud">
                  <div className="tracked text-[8px] text-white/35">Pouch</div>
                  <div
                    className="mono text-[15px] leading-none text-[var(--stage-gold)]"
                    data-testid="pouch-balance"
                  >
                    {fmtAxvTrim(me?.pouch_sub || 0)}
                  </div>
                </div>
                <LiveToggle accent="cyan" surface="cove" />
              </div>
            </div>

            {/* mode strip */}
            <div className="flex gap-1.5 px-6 py-3 md:px-12">
              {["Clarify", "Explore", "Challenge", "Refine", "Plan"].map((m) => (
                <span
                  key={m}
                  className={`tracked rounded-sm border px-1.5 py-0.5 text-[7px] transition-colors duration-300 ${
                    mode === m
                      ? "border-[#7fd8ef]/70 bg-[#7fd8ef]/10 text-[#bfe6ff]"
                      : "border-white/10 text-white/25"
                  }`}
                  data-testid={mode === m ? "melzai-mode" : undefined}
                >
                  {m}
                </span>
              ))}
            </div>

            {/* MelzAI speaks, centre screen */}
            <div
              ref={scroller}
              className="grid flex-1 gap-10 overflow-y-auto px-6 py-6 md:px-12 md:py-10 lg:grid-cols-[1.45fr_1fr] lg:items-center"
              data-testid="cove-transcript"
            >
              <div>
              {busy ? (
                <div className="mono flex items-center gap-2 py-6 text-[10px] text-[#bfe6ff]/70">
                  <Loader2 size={11} className="animate-spin" /> considering it
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.p
                    key={lastMelz?.text}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="serif text-[19px] leading-relaxed text-[#e8f4fb] md:text-[26px] lg:text-[30px]"
                    data-testid="cove-turn-melz"
                  >
                    {lastMelz?.text}
                  </motion.p>
                </AnimatePresence>
              )}

              </div>

              {/* the Way, as an on-screen readout */}
              <div className="space-y-3 border-white/10 pt-5 max-lg:border-t lg:border-l lg:pl-8 lg:pt-0">
                <div className="tracked mb-4 text-[8px] text-white/35">The Way</div>
                {[
                  ["Horizon", null, "what", null],
                  ["Slice", "That", "slice", "that"],
                  ["Where", "There", "where", "there"],
                  ["By", "Then", "when", "then"],
                ].map(([vl, sl, vk, sk]) => (
                  <div
                    key={vk}
                    className={`grid grid-cols-[52px_1fr] gap-2 border-b border-white/[0.06] pb-2.5 last:border-0 ${
                      vk === "slice" ? "border-l-2 border-l-[var(--stage-gold)]/50 pl-2" : ""
                    }`}
                  >
                    <div
                      className={`mono pt-0.5 text-[8px] uppercase ${
                        vk === "slice" ? "text-[var(--stage-gold)]/80" : "text-white/30"
                      }`}
                    >
                      {vl}
                    </div>
                    <div>
                      <div
                        className={`leading-snug ${
                          vk === "slice"
                            ? "text-[13.5px] text-[#f7e6c8] md:text-[15px]"
                            : "text-[12.5px] text-white/55 md:text-[14px]"
                        }`}
                        data-testid={`way-${vk}`}
                      >
                        {way[vk] || "—"}
                      </div>
                      {vk === "when" && way.deadline && (
                        <div className="mono mt-1 text-[9px] text-[var(--arcade-cyan)]" data-testid="way-deadline">
                          deadline {new Date(way.deadline).toLocaleDateString()}
                        </div>
                      )}
                      {sk && way[sk] && (
                        <div className="mt-1 flex gap-1.5">
                          <span className="mono shrink-0 text-[8px] uppercase text-[var(--stage-gold)]/80">
                            {sl}
                          </span>
                          <span
                            className="text-[12.5px] leading-snug text-[#f7e6c8] md:text-[14px]"
                            data-testid={`way-${sk}`}
                          >
                            {way[sk]}
                          </span>
                        </div>
                      )}
                      {sk && !way[sk] && (
                        <span className="hidden" data-testid={`way-${sk}`}>
                          —
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* game menu — the only place you act */}
            <div className="border-t border-white/10 px-6 pb-5 pt-4 md:px-12 md:pb-8 md:pt-5">
              {complete ? (
                <button
                  type="button"
                  data-testid="cut-playcard-button"
                  onClick={cutPlayCard}
                  disabled={cutting}
                  className="tracked flex w-full items-center justify-center gap-2 rounded-sm border border-[var(--stage-gold)]/60 bg-[var(--stage-gold)]/12 py-4 text-[11px] font-semibold text-[#f8e6c4] transition-colors duration-200 hover:bg-[var(--stage-gold)]/22"
                >
                  {cutting ? <Loader2 size={13} className="animate-spin" /> : "▸ Cut the PlayCard"}
                </button>
              ) : (
                <>
                  {cadences.length > 0 ? (
                    <div
                      className="grid grid-cols-2 gap-1.5 md:grid-cols-3"
                      data-testid="cadence-chips"
                    >
                      {cadences.map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          data-testid={`cadence-${c.key}`}
                          disabled={busy}
                          onClick={() => send(c.name)}
                          className="group rounded-sm border border-white/10 px-3 py-2 text-left transition-colors duration-150 hover:border-[var(--stage-gold)]/60 hover:bg-[var(--stage-gold)]/10 disabled:opacity-40"
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="text-[13px] text-white/75 transition-colors group-hover:text-[#f7e6c8]">
                              {c.name}
                            </span>
                            <span className="mono shrink-0 text-[8px] text-[var(--arcade-cyan)]/80">
                              {c.window}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[10px] leading-snug text-white/30">
                            {c.blurb}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {chips.map((c) => (
                        <button
                          key={c}
                          type="button"
                          data-testid="cove-suggestion"
                          disabled={busy}
                          onClick={() => send(c)}
                          className="group flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-[14px] text-white/60 md:text-[16px] transition-colors duration-150 hover:bg-[#7fd8ef]/10 hover:text-[#d6f4ff] disabled:opacity-40"
                        >
                          <ChevronRight
                            size={11}
                            className="shrink-0 text-[#7fd8ef]/0 transition-colors duration-150 group-hover:text-[#7fd8ef]"
                          />
                          <span className="leading-snug">{c}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <form
                    className="mt-2.5 flex items-center gap-2 border-t border-white/10 pt-2.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!draft.trim()) return;
                      send(draft.trim());
                      setDraft("");
                    }}
                  >
                    <span className="mono shrink-0 text-[11px] text-[#7fd8ef]">&gt;</span>
                    <input
                      className="mono min-w-0 flex-1 bg-transparent text-[14px] text-[#e8f4fb] placeholder-white/25 outline-none"
                      data-testid="cove-input"
                      placeholder={
                        live && !liveSpent
                          ? "live · ask him something no canned demo could hold…"
                          : cadences.length
                          ? "or name your own window…"
                          : stage === "slice"
                          ? "or cut your own Slice…"
                          : "or type it your own way…"
                      }
                      value={draft}
                      disabled={busy}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <button
                      type="submit"
                      data-testid="cove-send"
                      disabled={busy || !draft.trim()}
                      className="shrink-0 text-[#7fd8ef]/70 transition-colors duration-150 hover:text-[#7fd8ef] disabled:opacity-30"
                    >
                      <CornerDownLeft size={13} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </Screen>

        </motion.div>
      )}
    </div>
  );
}
